import { useState, type FormEvent } from "react";

const FORM_ACTION = "https://readdy.ai/api/form/db7sj77idngsrf4ehp5g";

type Status = "idle" | "submitting" | "success" | "error";

export default function FeedbackForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const payload = new FormData(form);

    const honeypot = String(payload.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      form.reset();
      return;
    }
    payload.delete("website_alt");

    setStatus("submitting");
    setFormError("");

    try {
      const body = new URLSearchParams();
      payload.forEach((value, key) => body.append(key, String(value)));

      const response = await fetch(FORM_ACTION, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = String(serverMsg).toLowerCase().includes("spam");
      const success = response.ok && parsed?.code === "OK" && !isSpam;

      if (success) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(serverMsg || "提交失败，请稍后重试");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交未成功，请稍后再试");
    }
  };

  return (
    <form
      id="feedback-form"
      data-readdy-form
      onSubmit={handleSubmit}
      className="rounded-lg border border-background-200/70 bg-background-50 p-5 md:p-7"
    >
      <h2 className="font-heading text-lg font-semibold text-foreground-900">留言与反馈</h2>
      <p className="mt-2 text-sm text-foreground-500">
        对本站内容有建议，或想补充某段剧情、角色资料？欢迎给我们留言。
      </p>

      <div className="mt-5 grid gap-4 sm:grid-cols-2">
        <label className="flex flex-col gap-1.5">
          <span className="text-xs text-foreground-500">姓名</span>
          <input
            name="name"
            type="text"
            required
            placeholder="请输入你的称呼"
            className="rounded-md border border-background-300/80 bg-background-50 px-3 py-2 text-sm text-foreground-900 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
          />
        </label>
        <label className="flex flex-col gap-1.5">
          <span className="text-xs text-foreground-500">邮箱</span>
          <input
            name="email"
            type="email"
            required
            placeholder="用于接收回复的邮箱"
            className="rounded-md border border-background-300/80 bg-background-50 px-3 py-2 text-sm text-foreground-900 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
          />
        </label>
      </div>

      <label className="mt-4 flex flex-col gap-1.5">
        <span className="text-xs text-foreground-500">反馈类型</span>
        <select
          name="topic"
          defaultValue="内容建议"
          className="rounded-md border border-background-300/80 bg-background-50 px-3 py-2 text-sm text-foreground-900 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
        >
          <option value="内容建议">内容建议</option>
          <option value="资料纠错">资料纠错</option>
          <option value="片段需求">片段需求</option>
          <option value="其他反馈">其他反馈</option>
        </select>
      </label>

      <label className="mt-4 flex flex-col gap-1.5">
        <span className="text-xs text-foreground-500">反馈内容</span>
        <textarea
          name="message"
          required
          maxLength={500}
          rows={5}
          placeholder="请简要描述你的建议或想补充的内容（500 字以内）"
          className="resize-none rounded-md border border-background-300/80 bg-background-50 px-3 py-2 text-sm leading-relaxed text-foreground-900 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
        ></textarea>
      </label>

      <input
        className="form-field-alt"
        type="text"
        name="website_alt"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
      />

      <div className="mt-5 flex flex-wrap items-center gap-3">
        <button
          type="submit"
          disabled={status === "submitting"}
          className="inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm text-background-50 transition hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <i className={status === "submitting" ? "ri-loader-4-line animate-spin" : "ri-send-plane-line"}></i>
          {status === "submitting" ? "提交中…" : "提交反馈"}
        </button>
        {status === "success" && (
          <span className="flex items-center gap-1.5 text-sm text-primary-600">
            <i className="ri-checkbox-circle-line"></i>提交成功，感谢你的反馈！
          </span>
        )}
        {status === "error" && (
          <span className="flex items-center gap-1.5 text-sm text-primary-700">
            <i className="ri-error-warning-line"></i>
            {formError}
          </span>
        )}
      </div>
    </form>
  );
}